// Typed fetch helpers for the player profile endpoints.
//
// All requests use `credentials: 'include'` so the signed player_id
// cookie set by POST /api/player/create rides along. The server
// requires it on every protected endpoint (auth/session.ts:requirePlayer).

import type {
  CreatePlayerRequest,
  PlayerProfile,
} from '@origami/shared'

/** Discriminated-union result type used across both player and solo
 *  fetchers. `ok: false` carries the typed error code so call sites
 *  can branch (e.g. SoloPlayScreen routes 'no_session' →
 *  PlayerCreationScreen). */
export type ApiResult<T> =
  | { ok: true; value: T }
  | { ok: false; code: string; message?: string; status: number }

async function asResult<T>(res: Response): Promise<ApiResult<T>> {
  if (res.ok) {
    return { ok: true, value: (await res.json()) as T }
  }
  let code = 'unknown'
  let message: string | undefined
  try {
    const body = (await res.json()) as { error?: { code?: string; message?: string } }
    code = body?.error?.code ?? code
    message = body?.error?.message
  } catch {
    // body not JSON — keep default code
  }
  return { ok: false, code, message, status: res.status }
}

/** Module-scope cache for the most recent successful getMe() — lets
 *  callers (SoloRoute, MultiRoute) read the player profile
 *  synchronously on remount without re-firing the network probe
 *  every time. Causes a visible "loading…" flash otherwise when the
 *  user navigates back and forth between the title and ?multi. The
 *  cache is invalidated explicitly by logout / create. */
let cachedPlayer: PlayerProfile | null = null

export function getCachedPlayer(): PlayerProfile | null {
  return cachedPlayer
}

export function clearCachedPlayer(): void {
  cachedPlayer = null
}

/** GET /api/player/me — used on page load to decide between the
 *  creation form (401) and the resume-session path (200). On
 *  success the result is cached in module scope so subsequent
 *  callers (e.g. re-entering ?multi) don't re-probe. */
export async function getMe(): Promise<ApiResult<{ player: PlayerProfile }>> {
  const res = await fetch('/api/player/me', { credentials: 'include' })
  const result = await asResult<{ player: PlayerProfile }>(res)
  if (result.ok) cachedPlayer = result.value.player
  else if (result.status === 401) cachedPlayer = null
  return result
}

/** POST /api/player/create — submits the new-player form. On success
 *  the server sets the signed player_id cookie automatically. */
export async function createPlayer(
  body: CreatePlayerRequest,
): Promise<ApiResult<{ player: PlayerProfile }>> {
  const res = await fetch('/api/player/create', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  const result = await asResult<{ player: PlayerProfile }>(res)
  if (result.ok) cachedPlayer = result.value.player
  return result
}

/** POST /api/player/logout — clears the signed player_id cookie. After
 *  this call, subsequent /api/player/me requests return 401 and the
 *  client routes to PlayerCreationScreen for a fresh identity. */
export async function logoutPlayer(): Promise<ApiResult<{ ok: true }>> {
  const res = await fetch('/api/player/logout', {
    method: 'POST',
    credentials: 'include',
  })
  cachedPlayer = null
  return asResult(res)
}
